import { AnimationEvent } from "@angular/animations";
import { FocusMonitor, FocusTrapFactory, InteractivityChecker } from "@angular/cdk/a11y";
import { OverlayRef } from "@angular/cdk/overlay";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	NgZone,
	Optional,
	ViewEncapsulation,
} from "@angular/core";
import {
	_MatLegacyDialogContainerBase as _MatDialogContainerBase,
	MatLegacyDialogConfig as MatDialogConfig,
} from "@angular/material/legacy-dialog";
import { matSheetAnimations } from "./mat-right-side-sheet-animations";

@Component({
	selector: "mat-right-side-sheet-container",
	templateUrl: "./mat-right-side-sheet.component.html",
	styleUrls: ["./mat-right-side-sheet.component.scss"],
	encapsulation: ViewEncapsulation.None,
	// Using OnPush for dialogs caused some G3 sync issues. Disabled until we can track them down.
	// tslint:disable-next-line:validate-decorators
	changeDetection: ChangeDetectionStrategy.Default,
	animations: [matSheetAnimations.sheetContainer],
	host: {
		class: "mat-sheet-container",
		tabindex: "-1",
		"aria-modal": "true",
		"[id]": "_id",
		"[attr.role]": "_config.role",
		"[attr.aria-labelledby]": "_config.ariaLabel ? null : _ariaLabelledBy",
		"[attr.aria-label]": "_config.ariaLabel",
		"[attr.aria-describedby]": "_config.ariaDescribedBy || null",
		"[@sheetContainer]": "_state",
		"(@sheetContainer.start)": "_onAnimationStart($event)",
		"(@sheetContainer.done)": "_onAnimationDone($event)",
	},
})
export class MatRightSideSheetComponent extends _MatDialogContainerBase {
	constructor(
		elementRef: ElementRef,
		focusTrapFactory: FocusTrapFactory,
		@Optional() @Inject(DOCUMENT) document: any,
		dialogConfig: MatDialogConfig,
		checker: InteractivityChecker,
		ngZone: NgZone,
		overlayRef: OverlayRef,
		protected _changeDetectorRef: ChangeDetectorRef,
		focusMonitor?: FocusMonitor
	) {
		super(elementRef, focusTrapFactory, document, dialogConfig, checker, ngZone, overlayRef, focusMonitor);
	}

	_getAnimationState() {
		return {
			value: this._state,
			params: {
				enterAnimationDuration: this._config.enterAnimationDuration,
				exitAnimationDuration: this._config.exitAnimationDuration,
			},
		};
	}
	_startExitAnimation(): void {
		this._state = "exit";

		// Mark the container for check so it can react if the
		// view container is using OnPush change detection.
		this._changeDetectorRef.markForCheck();
	}
	/** State of the dialog animation. */
	_state: "void" | "enter" | "exit" = "enter";

	/** Callback, invoked whenever an animation on the host completes. */
	_onAnimationDone({ toState, totalTime }: AnimationEvent) {
		if (toState === "enter") {
			this._openAnimationDone(totalTime);
		} else if (toState === "exit") {
			this._animationStateChanged.next({ state: "closed", totalTime });
		}
	}

	/** Callback, invoked when an animation on the host starts. */
	_onAnimationStart({ toState, totalTime }: AnimationEvent) {
		if (toState === "enter") {
			this._animationStateChanged.next({ state: "opening", totalTime });
		} else if (toState === "exit" || toState === "void") {
			this._animationStateChanged.next({ state: "closing", totalTime });
		}
	}
}
